import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { MemberDTO } from '../../../shared/dto/members';
import { Avatar, SECTION_COLOR } from '../../components/people';
import { useT, type MessageKey } from '../../i18n';
import type { GroupKey } from './groups';

export function groupLabelKey(key: GroupKey): MessageKey {
  if (key === 'other') return 'choir.group_other';
  if (key === 'musician') return 'section.musicians';
  return `section.${key}`;
}

/** Header bar / count-pill colour. "Aðrir" uses the same dark fallback as Avatar (white text stays readable in dark mode). */
export function groupColor(key: GroupKey): string {
  return key === 'other' ? 'var(--color-musician)' : SECTION_COLOR[key];
}

export function TitleBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center truncate rounded-full bg-[var(--accent-soft)] px-2.5 py-0.5 text-[14px] font-semibold text-[var(--accent)]">
      {children}
    </span>
  );
}

export function LeaveBadge() {
  const t = useT();
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[14px] font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
      {t('choir.on_leave')}
    </span>
  );
}

/** Directory row: avatar, name, title / leave badges, chevron. Whole row is one ≥ 64 px link. */
export function MemberRow({ m, to, isMe, trailing }: { m: MemberDTO; to: string; isMe?: boolean; trailing?: ReactNode }) {
  const t = useT();
  return (
    <li>
      <Link
        to={to}
        className="flex min-h-16 items-center gap-3 px-4 py-2.5 transition hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)]"
      >
        <Avatar userId={m.id} name={m.fullName} rev={m.avatarRev} section={m.section} size={48} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[17px] font-semibold">
            {m.fullName}
            {isMe ? <span className="ml-2 text-[14px] font-medium text-[var(--muted)]">({t('choir.you')})</span> : null}
          </div>
          {m.title || m.status === 'leave' || m.status === 'invited' || trailing ? (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {m.title ? <TitleBadge>{m.title}</TitleBadge> : null}
              {m.status === 'leave' ? <LeaveBadge /> : null}
              {m.status === 'invited' ? <span className="text-[14px] text-[var(--muted)]">{t('choir.invited')}</span> : null}
              {trailing}
            </div>
          ) : null}
        </div>
        <ChevronRight className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
      </Link>
    </li>
  );
}
